import { useState, useEffect } from 'react';
import api from '../api';
import { exportPNG, exportPDF } from '../services/ExportService';

export default function useSprintHistory({ showToast, projectId, userProfile }) {
  const [sprints, setSprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSprint, setSelectedSprint] = useState(null);
  const [sprintItems, setSprintItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteAll, setShowDeleteAll] = useState(false);
  const [editingHistoryItem, setEditingHistoryItem] = useState(null);

  const isAdmin = userProfile?.role === 'admin';

  useEffect(() => {
    const load = async () => {
      try {
        const closed = await api.getClosedSprints(projectId);
        setSprints(closed);
      } catch (err) {
        showToast(err.message || 'Failed to load sprint history', 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [showToast, projectId]);

  const handleSelectSprint = async (sprint) => {
    setSelectedSprint(sprint);
    setLoadingItems(true);
    try {
      const items = await api.getSprintItems(sprint.id);
      setSprintItems(items);
    } catch (err) {
      showToast(err.message || 'Failed to load sprint items', 'error');
    } finally {
      setLoadingItems(false);
    }
  };

  const handleDeleteSprint = async () => {
    if (!deleteTarget) return;
    try {
      await api.deleteClosedSprint(deleteTarget);
      setSprints((prev) => prev.filter((s) => s.id !== deleteTarget));
      showToast('Sprint deleted', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to delete sprint', 'error');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleDeleteAll = async () => {
    try {
      await api.deleteAllClosedSprints(projectId);
      setSprints([]);
      showToast('All history deleted', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to delete history', 'error');
    } finally {
      setShowDeleteAll(false);
    }
  };

  const handleExport = async (elementId, format) => {
    setExporting(true);
    try {
      if (format === 'png') await exportPNG(elementId);
      else await exportPDF(elementId);
    } finally {
      setExporting(false);
    }
  };

  const handleUpdateHistoryItem = async (itemId, data) => {
    try {
      const updated = await api.updateSprintItem(itemId, data);
      setSprintItems((prev) => prev.map((i) => (i.id === itemId ? updated : i)));
      setEditingHistoryItem(null);
      showToast('Note updated', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to update note', 'error');
    }
  };

  const handleDeleteHistoryItem = async (itemId) => {
    try {
      await api.deleteSprintItem(itemId);
      setSprintItems((prev) => prev.filter((i) => i.id !== itemId));
      showToast('Note deleted', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to delete note', 'error');
    }
  };

  return {
    sprints,
    loading,
    selectedSprint,
    setSelectedSprint,
    sprintItems,
    loadingItems,
    exporting,
    deleteTarget,
    setDeleteTarget,
    showDeleteAll,
    setShowDeleteAll,
    isAdmin,
    handleSelectSprint,
    handleDeleteSprint,
    handleDeleteAll,
    handleExport,
    editingHistoryItem,
    setEditingHistoryItem,
    handleUpdateHistoryItem,
    handleDeleteHistoryItem,
  };
}
